import { useEffect, type ReactNode } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { useGetKeywordsChartsQuery } from '@/app/KeywordTracker/api/keywordsApi';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import TimePicker from '../components/common/TimePicker';
import Flex from '@/components/common/Flex';
import IconItem from '@/components/common/icon/IconItem';
import barChart from '@/assets/img/illustrations/crm-bar-chart.png';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import CardChartCircleLoading from '@/app/common/components/skeleton/CardChartCircleLoading';
import StatisticsCard from '@/app/KeywordTracker/components/keywords/overview/StatisticsCards';
import CustomerSatisfaction from '../components/keywords/overview/CustomerSatisfaction';
import CardDistributionRanks from '../components/keywords/overview/CardDistributionRanks';
import TopKeywords from '../components/keywords/overview/TopKeywords';
import Audience from '../components/keywords/overview/Audience';
import NumberOfTickets from '../components/keywords/overview/NumberOfTickets';
import CardSOVCircleChart from '../components/keywords/overview/CardSOVCircleChart';
import EcomStat from '../components/keywords/overview/EcomStat';
import { setFilters } from '@/app/common/reducers/filterSlice';
import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import CardRadarChart from '../components/keywords/overview/CardDistributionRanksRadarChart';

import type { OutletContext } from './pageTypes';

function WidgetSectionTitle({
  title,
  subtitle,
  icon
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon: Parameters<typeof IconItem>[0]['icon'];
}) {
  return (
    <Flex className="d-flex align-items-start gap-2 mb-4 mt-3">
      <IconItem
        tag="div"
        icon={icon}
        bg="200"
        color="primary"
        size="sm"
        iconClass="fs--2"
        className="shadow-none"
      />
      <div className="d-flex flex-column">
        <h5 className="mb-0 text-700 lh-base">{title}</h5>
        {subtitle ? <p className="mb-0 fs--2 text-500">{subtitle}</p> : null}
      </div>
    </Flex>
  );
}

const Overview = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const dispatch = useDispatch();
  useGetUserInfoQuery();
  const { domain_slug } = useDomain();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  useSearchParams();

  useEffect(() => {
    setBreadcrumbData([{ text: 'نمای کلی', href: '#' }]);
    dispatch(setFilters({}));
  }, [dispatch, setBreadcrumbData]);

  const { data: chartsData } = useGetKeywordsChartsQuery({ domain_slug });

  return (
    <>
      <Card className="bg-100 shadow-none border mb-4">
        <Card.Body className="p-3 p-md-4">
          <Row className="g-3 align-items-center justify-content-between">
            <Col xs={12} md="auto">
              <Flex alignItems="center" className="gap-3">
                <img
                  src={barChart}
                  width={72}
                  alt="..."
                  className="shadow-none"
                />
                <div className="d-flex flex-column">
                  <h6 className="text-700 mb-0 text-nowrap">بازه زمانی</h6>
                  <p className="mb-0 fs--2 text-500">برای محاسبه نمودارها</p>
                </div>
              </Flex>
            </Col>

            <Col xs={12} md="auto">
              {chartsData?.data?.average_position?.update_dates?.length ? (
                <TimePicker
                  initDay={
                    chartsData?.data?.average_position?.update_dates?.at(0)
                  }
                  lastDay={
                    chartsData?.data?.average_position?.update_dates?.at(-1)
                  }
                />
              ) : (
                <TextLoading width={isMdUp ? 205 : 100} height={24} isWidthPx={isMdUp} />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <WidgetSectionTitle
        icon="chart-line"
        title="آمار کلیدی"
        subtitle="KPI های مهم در یک نگاه"
      />
      <Row className="g-3 mb-3">
        <Col md={12}>
          <StatisticsCard />
        </Col>
      </Row>

      <WidgetSectionTitle
        icon="crown"
        title="کلمات برتر"
        subtitle="رشد و وضعیت ورود/خروج"
      />
      <Row className="g-3 mb-3">
        <Col lg={6}>
          <TopKeywords />
        </Col>
        <Col lg={6}>
          <EcomStat />
        </Col>
      </Row>

      <WidgetSectionTitle
        icon="chart-pie"
        title="تحلیل رتبه و توزیع"
        subtitle="روند رتبه ها و توزیع رتبه کلمات"
      />
      <Row className="g-3 mb-3">
        <Col lg={4}>
          {chartsData ? (
            <CardRadarChart
              title="روند رتبه ها"
              tooltip={'Destruction'}
              data={chartsData?.data?.distribution_ranks}
            />
          ) : (
            <CardChartCircleLoading />
          )}
        </Col>
        <Col lg={8}>
          <CardDistributionRanks title="توزیع رتبه کلمات" />
        </Col>
      </Row>

      <WidgetSectionTitle
        icon="users"
        title="روندها و مخاطبان"
        subtitle="رشد/افت، رفتار کاربران و SOV"
      />
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <CustomerSatisfaction />
        </Col>
        <Col lg={8}>
          <NumberOfTickets />
        </Col>
      </Row>
      <Row className="g-3 mb-3">
        <Col lg={4}>
          <CardSOVCircleChart />
        </Col>
        <Col lg={8}>
          <Audience />
        </Col>
      </Row>
    </>
  );
};

export default Overview;
